import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { Button } from '@/components/ui/Button'
import { AdminSidebar } from '@/components/layout/AdminSidebar'
import { cn } from '@/lib/utils'

interface EpisodeForm {
  id?: number; title?: string; slug?: string; description?: string
  body?: string; video_url?: string; thumbnail_url?: string; duration_seconds?: number
  is_free?: boolean; difficulty?: string; status?: string
  published_at?: string; position?: number; series_id?: number
  tags?: string[]
}

interface Props {
  episode: EpisodeForm | null
  series_list: { id: number; title: string }[]
  all_tags: { id: number; name: string }[]
  errors?: string[]
}

export default function Form({ episode, series_list, all_tags, errors }: Props) {
  const isEdit = !!episode?.id
  const [form, setForm] = useState({
    title: episode?.title ?? '',
    description: episode?.description ?? '',
    body: episode?.body ?? '',
    video_url: episode?.video_url ?? '',
    thumbnail_url: episode?.thumbnail_url ?? '',
    duration: String(episode?.duration_seconds ?? ''),
    is_free: episode?.is_free ?? false,
    difficulty: episode?.difficulty ?? 'beginner',
    status: episode?.status ?? 'draft',
    published_at: episode?.published_at ?? '',
    series_id: String(episode?.series_id ?? ''),
    position: String(episode?.position ?? ''),
    tags: episode?.tags ?? [] as string[],
  })
  const [tagInput, setTagInput] = useState('')

  const set = (key: string, val: string | boolean | string[]) =>
    setForm(prev => ({ ...prev, [key]: val }))

  const addTag = () => {
    const t = tagInput.trim().toLowerCase()
    if (t && !form.tags.includes(t)) set('tags', [...form.tags, t])
    setTagInput('')
  }

  const removeTag = (t: string) => set('tags', form.tags.filter(x => x !== t))

  const handleSubmit = (status: 'draft' | 'published') => {
    const payload = { ...form, status, tag_names: form.tags }
    if (isEdit) {
      router.patch(`/admin/episodes/${episode!.slug}`, payload)
    } else {
      router.post('/admin/episodes', payload)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <AdminSidebar active="/admin/episodes" />

      <main className="flex-1 p-8">
        {/* Breadcrumb */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-sm text-gray-400 mb-1">
              <Link href="/admin/episodes" className="hover:underline">Episodes</Link>
              <span className="mx-1">›</span>
              {isEdit ? 'Edit episode' : 'New episode'}
            </p>
            <h1 className="text-2xl font-bold text-gray-900">{isEdit ? 'Edit episode' : 'New episode'}</h1>
          </div>
          <div className="flex gap-2">
            <Link href="/admin/episodes">
              <Button variant="secondary">Cancel</Button>
            </Link>
            <Button variant="secondary" onClick={() => handleSubmit('draft')}>Save draft</Button>
            <Button onClick={() => handleSubmit('published')} className="gap-1">
              🚀 Publish episode
            </Button>
          </div>
        </div>

        {errors && errors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
            {errors.map((e, i) => <p key={i} className="text-sm text-red-600">{e}</p>)}
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main form */}
          <div className="lg:col-span-2 space-y-5">
            <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-5">
              <h3 className="font-semibold text-gray-900">Episode details</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  value={form.title}
                  onChange={e => set('title', e.target.value)}
                  className="w-full px-4 py-2.5 text-sm border border-brand rounded-xl focus:outline-none focus:ring-2 focus:ring-brand/20"
                  placeholder="Episode title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description <span className="text-gray-400 font-normal">(shown on episode cards)</span></label>
                <textarea
                  value={form.description}
                  onChange={e => set('description', e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand/20 resize-none"
                  placeholder="One-paragraph summary shown in search results and episode cards."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Show Notes (Markdown)</label>
                <textarea
                  value={form.body}
                  onChange={e => set('body', e.target.value)}
                  rows={8}
                  className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand/20 font-mono resize-y"
                  placeholder="In this episode we..."
                />
              </div>
            </div>

            {/* Video */}
            <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-4">
              <h3 className="font-semibold text-gray-900">Video</h3>
              <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center">
                <p className="text-gray-400 text-sm mb-1">Drop video file or paste Cloudflare R2 URL</p>
                <p className="text-xs text-gray-300">MP4 / HLS supported · max 4 GB</p>
              </div>
              <input
                value={form.video_url}
                onChange={e => set('video_url', e.target.value)}
                placeholder="https://r2.getcasts.io/episodes/..."
                className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand/20 font-mono"
              />
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Thumbnail URL</label>
                <input
                  value={form.thumbnail_url}
                  onChange={e => set('thumbnail_url', e.target.value)}
                  placeholder="https://r2.getcasts.io/thumbnails/..."
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand/20 font-mono"
                />
                {form.thumbnail_url && (
                  <img src={form.thumbnail_url} alt="thumbnail preview" className="mt-2 rounded-lg h-20 object-cover" />
                )}
              </div>
            </div>
          </div>

          {/* Sidebar metadata */}
          <div className="space-y-5">
            {/* Metadata */}
            <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
              <h3 className="font-semibold text-gray-900">Metadata</h3>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Duration <span className="font-normal">(seconds)</span></label>
                <input
                  type="number"
                  value={form.duration}
                  onChange={e => set('duration', e.target.value)}
                  min={0}
                  placeholder="e.g. 720"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none font-mono"
                />
                {form.duration && Number(form.duration) > 0 && (
                  <p className="text-xs text-gray-400 mt-1">
                    {Math.floor(Number(form.duration) / 60)}m {Number(form.duration) % 60}s
                  </p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Difficulty</label>
                <select
                  value={form.difficulty}
                  onChange={e => set('difficulty', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none"
                >
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Series</label>
                <select
                  value={form.series_id}
                  onChange={e => set('series_id', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none"
                >
                  <option value="">None</option>
                  {series_list.map(s => (
                    <option key={s.id} value={s.id}>{s.title}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Position in series</label>
                <input
                  type="number"
                  value={form.position}
                  onChange={e => set('position', e.target.value)}
                  min={1}
                  placeholder="e.g. 1"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none font-mono"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Tags</label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {form.tags.map(t => (
                    <span key={t} className="flex items-center gap-1 text-xs bg-brand/10 text-brand px-2 py-0.5 rounded-full">
                      {t}
                      <button onClick={() => removeTag(t)} className="text-brand/60 hover:text-brand">×</button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    value={tagInput}
                    onChange={e => setTagInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    placeholder="Add tag..."
                    className="flex-1 px-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none"
                  />
                  <button onClick={addTag} className="text-xs text-brand hover:underline">Add</button>
                </div>
              </div>
            </div>

            {/* Publish */}
            <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-3">
              <h3 className="font-semibold text-gray-900">Publish</h3>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
                <select
                  value={form.status}
                  onChange={e => set('status', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none"
                >
                  <option value="draft">Draft</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="published">Published</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Publish date / time</label>
                <input
                  type="datetime-local"
                  value={form.published_at ? form.published_at.slice(0, 16) : ''}
                  onChange={e => set('published_at', e.target.value ? new Date(e.target.value).toISOString() : '')}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none"
                />
                <p className="text-xs text-gray-400 mt-1">Leave blank to publish immediately.</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-2">Access</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => set('is_free', true)}
                    className={cn('flex-1 py-1.5 text-sm rounded-lg border transition-colors', form.is_free ? 'border-brand bg-brand text-white' : 'border-gray-300 text-gray-700 bg-white')}
                  >
                    Free
                  </button>
                  <button
                    onClick={() => set('is_free', false)}
                    className={cn('flex-1 py-1.5 text-sm rounded-lg border transition-colors', !form.is_free ? 'border-brand bg-brand text-white' : 'border-gray-300 text-gray-700 bg-white')}
                  >
                    Pro
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
